/**
 * Medications Section for Resident Detail Dialog
 *
 * Displays current medications with status and details.
 */

import { Pill, Calendar, AlertTriangle } from "lucide-react";
import { MedicationsResponse } from "../../../types/residents";
import { formatDate } from "../../../utils/dateFormat";

interface MedicationsSectionProps {
  data: MedicationsResponse | null;
  isLoading: boolean;
  error: Error | null;
  newOnly?: boolean;  // Track if filtering to new medications only
  window?: number;  // Window value for display in messages
}

export function MedicationsSection({
  data,
  isLoading,
  error,
  newOnly = true,  // Default matches API default
  window = 3,  // Default newInsights window
}: MedicationsSectionProps) {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading medications...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading medications: {error.message}
        </div>
      );
    }

    if (!data || data.medications.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          {newOnly
            ? `No new medications (started within last ${window} days)`
            : "No medications on record"
          }
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#228189] rounded-t-lg">
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                MEDICATION
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                DOSAGE & FREQUENCY
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                ROUTE
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                START DATE
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                PRESCRIBER
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-sm font-bold text-white uppercase"
              >
                INDICATION
              </th>
            </tr>
          </thead>
          <tbody>
            {data.medications.map((med, index) => (
              <tr key={med.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-4 text-base font-bold text-gray-900">
                  <div className="flex items-center gap-2">
                    <span>{med.name}</span>
                    {med.is_high_risk && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700"
                        title="Flagged as high-risk by the High-Risk Medications risk factor"
                      >
                        <AlertTriangle className="w-3 h-3" />
                        High Risk
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-900">{med.dosage ?? "—"}</span>
                    <span className="text-sm text-gray-600">{med.frequency ?? "—"}</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  {med.route_description ?? med.route ?? "—"}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-900">{formatDate(med.start_date)}</span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  {med.prescriber ?? "—"}
                </td>
                <td className="px-4 py-4 text-sm text-gray-900">
                  {med.indication ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Pill className="w-5 h-5 text-gray-700" />
        <h3 className="text-lg font-semibold text-gray-900">New Medications</h3>
      </div>

      {renderContent()}
    </div>
  );
}
